import Edit from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { useProductCenterApi } from 'api';
import { unitEvcStatusQuery } from 'api/queries';
import { SectionButton } from 'library/components/controls/buttons/SectionButton';
import { CustomSeverityDialog } from 'library/components/controls/dialogs/CustomSeverityDialog';
import { useDialogState } from 'library/hooks/useDialogState';
import { Readonly } from 'library/types';
import { unitEditPageUrl } from 'main/routes/urls/RouteUrls';

import { UpdateEVCWarning } from './UpdateEVCWarning';

interface UnitEditConfigurationButtonProps extends Readonly {
	unitId: number;
	unitPublicId: string;
}

export const UnitEditConfigurationButton = ({
	isReadonly,
	unitId,
	unitPublicId,
}: UnitEditConfigurationButtonProps) => {
	const { t } = useTranslation([
		'unitOverview',
		'unitCreation',
		'common',
		'commonButton',
	]);
	const navigate = useNavigate();
	const { open, handleClose, handleOpen } = useDialogState();
	const {
		open: evcWarningOpen,
		handleClose: evcWarningHandleClose,
		handleOpen: evcWarningHandleOpen,
	} = useDialogState();

	const { api } = useProductCenterApi();

	const { data: unitEvcStatus } = useSuspenseQuery(
		unitEvcStatusQuery(api, unitId)
	);

	const unitEditPath = useMemo(
		() =>
			generatePath(unitEditPageUrl, {
				unitId: unitPublicId,
			}),
		[unitPublicId]
	);

	const alertUser = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (unitEvcStatus.unitControlSystemStatus === 'UpdateBlocked') {
			event.preventDefault();
			event.stopPropagation();
			handleOpen();
		} else if (unitEvcStatus.unitControlSystemStatus === 'Updated') {
			event.preventDefault();
			event.stopPropagation();
			evcWarningHandleOpen();
		} else {
			navigate(unitEditPath);
		}
	};

	const handleGoToEdit = useCallback(() => {
		handleClose();
		evcWarningHandleClose();
		navigate(unitEditPath);
	}, [handleClose, navigate, evcWarningHandleClose, unitEditPath]);

	return (
		<>
			<CustomSeverityDialog
				isOpen={open}
				headerText={t('unitOverview:edit-unit-configuration')}
				content={t(
					'unitOverview:update-EVC-blocked-status-window-text',
					{
						chassisId:
							unitEvcStatus.newControlSystemChassisId ?? '',
					}
				)}
				acceptButtonLabel={t('commonButton:ok')}
				declineButtonLabel={t('commonButton:cancel')}
				onDecline={handleClose}
				onAccept={handleGoToEdit}
			/>
			<UpdateEVCWarning
				unitId={unitId}
				isOpen={evcWarningOpen}
				onAccept={handleGoToEdit}
				onDecline={handleGoToEdit}
				onCancel={evcWarningHandleClose}
			/>
			{isReadonly ? (
				<Tooltip title={t('unitCreation:edit-not-available-text')}>
					<span>
						<SectionButton
							disabled={isReadonly}
							text={t('unitOverview:edit-unit-configuration')}
							icon={<Edit />}
						/>
					</span>
				</Tooltip>
			) : (
				<SectionButton
					text={t('unitOverview:edit-unit-configuration')}
					icon={<Edit />}
					isLink={true}
					onClick={alertUser}
					to={unitEditPath}
				/>
			)}
		</>
	);
};
